import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Tooltip } from 'reactstrap';

import Icon from '@/components/common/iconedLinks/Icon';

import styles from './ProgressBar.module.scss';

export const ProgressBar = (
  {
    containerClassName,
    percentProgress,
    withCursor, cursorValue,
    trackColor, progressIndicatorColor,
    leadingContent,
    trailingContent,
    centeredDescription = false
  }
) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (cursorValue) {
      setTooltipOpen(true);
    }
  }, [cursorValue]);

  const cursorClassName = [
    styles.cursor,
    percentProgress > 0 && percentProgress < 100 && styles['cursor-progress'],
    percentProgress === 100 && styles['cursor-full']
  ].filter(Boolean).join(' ');

  const contentContainerClassName = [
    styles['content-container'],
    centeredDescription && styles['centered-content-container']
  ].filter(Boolean).join(' ');

  const leadingContentClassName = [
    styles.content,
    centeredDescription ? styles['centered-leading-content'] : styles['leading-content']
  ].join(' ');

  const labelContainerClassName = [
    styles['label-container'],
    centeredDescription && styles['centered-label-container']
  ].filter(Boolean).join(' ');

  return (
    <div className={`${styles.container} ${containerClassName}`}>
      <div className={styles['progress-bar']}>
        {leadingContent?.icon && (
          <div
            style={{ backgroundColor: leadingContent?.iconBackgroundColor }}
            className={`${styles.icon} ${styles['icon-left']}`}
            data-test-id="leading-content-icon"
          >
            <Icon src={leadingContent.icon} alt="leading-icon" width={18} height={18} />
          </div>
        )}

        <div style={{ backgroundColor: trackColor }} className={styles.track} data-test-id="track">
          <div
            style={{ width: `${percentProgress}%`, backgroundColor: progressIndicatorColor }}
            className={styles['progress-indicator']}
            data-test-id="progress-indicator"
          />
          {withCursor && (
            <div style={{ left: `${percentProgress}%` }} className={cursorClassName} id="cursor" data-test-id="cursor">
              {cursorValue &&
                <Tooltip
                  placement="top"
                  target="cursor"
                  isOpen={tooltipOpen}
                  className={styles['cursor-value-tooltip']}
                >
                  {cursorValue}
                </Tooltip>
              }
            </div>
          )}
        </div>

        {trailingContent?.icon && (
          <div
            style={{ backgroundColor: trailingContent?.iconBackgroundColor }}
            className={`${styles.icon} ${styles['icon-right']}`}
            data-test-id="trailing-content-icon"
          >
            <Icon src={trailingContent.icon} alt="trailing-icon" width={18} height={18} />
          </div>
        )}
      </div>

      <div className={contentContainerClassName} data-test-id="content-container">
        {(leadingContent?.label || leadingContent?.paragraph) && (
          <div className={leadingContentClassName}>
            {leadingContent?.label && (
              <div className={labelContainerClassName}>
                <div className={styles.label} data-test-id="leading-content-label">{leadingContent.label}</div>
                {leadingContent?.badge && (
                  <span
                    style={{ backgroundColor: leadingContent.badge }}
                    className={styles.badge}
                    data-test-id="leading-content-badge"
                  />
                )}
              </div>
            )}
            {leadingContent?.paragraph && (
              <div className={styles.paragraph} data-test-id="leading-content-paragraph">{leadingContent.paragraph}</div>
            )}
          </div>
        )}

        {!centeredDescription && (trailingContent?.label || trailingContent?.paragraph) && (
          <div className={`${styles['content']} ${styles['trailing-content']}`}>
            {trailingContent?.label && (
              <div className={`${styles['label-container']} ${styles['align-left']}`}>
                {trailingContent?.badge && (
                  <span
                    style={{ backgroundColor: trailingContent.badge }}
                    className={styles.badge}
                    data-test-id="trailing-content-badge"
                  />
                )}
                <div className={styles.label} data-test-id="trailing-content-label">{trailingContent.label}</div>
              </div>
            )}
            {trailingContent?.paragraph && (
              <div className={styles.paragraph} data-test-id="trailing-content-paragraph">{trailingContent.paragraph}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  containerClassName: PropTypes.string,
  percentProgress: PropTypes.number,
  withCursor: PropTypes.bool,
  cursorValue: PropTypes.string,
  trackColor: PropTypes.string,
  progressIndicatorColor: PropTypes.string,
  leadingContent: PropTypes.shape({
    icon: PropTypes.string,
    iconBackgroundColor: PropTypes.string,
    label: PropTypes.string,
    badge: PropTypes.string,
    paragraph: PropTypes.string
  }),
  trailingContent: PropTypes.shape({
    icon: PropTypes.string,
    iconBackgroundColor: PropTypes.string,
    label: PropTypes.string,
    badge: PropTypes.string,
    paragraph: PropTypes.string
  }),
  centeredDescription: PropTypes.bool
};

export default ProgressBar;
